<template>
  <ion-page>
    <ion-content :scroll-events="true" @ion-scroll="handleScroll($event)">
      <div class="main-container">
        <div v-if="incident" class="h-full flex flex-col pb-320">
          <div class="px-16 pt-32 text-start">
            <h1 class="headline-xl">{{ optionalFieldsLabels.label }}</h1>
            <div class="text-sm text-dark mt-12">{{ optionalFieldsLabels.subLabel }}</div>
          </div>
          <div class="px-16 pt-16">
            <div
              v-for="field in optionalFields"
              :key="'obligatory-field-' + field.order"
              class="border-b border-light"
            >
              <div
                class="headline-medium select-none cursor-pointer flex justify-between py-16"
                :class="{
                  'text-error': formValidated && isFieldInvalid(field),
                }"
                @click="openItem(field.order)"
              >
                {{ field.label }}
                <div class="flex">
                  <ion-icon
                    v-if="formValidated && isFieldInvalid(field)"
                    :icon="alertCircleOutline"
                    class="mr-12 w-24 h-24 text-error"
                  />
                  <ion-icon
                    :icon="chevronDown"
                    class="mr-12 transition-all w-24 h-24 text-primary-default"
                    :class="{
                      'rotate-180': openAccordionItems.includes(field.order),
                    }"
                  />
                </div>
              </div>
              <div
                class="overflow-hidden transition-all duration-500"
                :class="{
                  'max-h-0 opacity-0': !openAccordionItems.includes(field.order),
                  'max-h-[3000px] opacity-100 md:mb-24 mb-16': openAccordionItems.includes(field.order)
                }"
              >
                <div>
                  <div class="text-sm text-start text-dark">{{ field.helptext }}</div>
                  <div class="pt-16">
                    <form>
                      <FieldInput
                        :field="field"
                        :show-validation="formValidated"
                        @on-input="(input) => onInput(field, input)"
                        @on-additional-input="(input) => inAdditionalInput(field, input)"
                        @on-repeater-input="(rows) => onRepeaterInput(field, rows)"
                        @on-repeater-add-row="() => onRepeaterAddRow(field)"
                        @on-repeater-remove-row="(rowIndex) => onRepeaterRemoveRow(field, rowIndex)"
                        @on-repeater-row-value-change="(payload) => onRepeaterRowValueChange(field, payload)"
                        @on-assets="(assets) => field.assets = assets"
                      />
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-auto px-16 py-24 flex justify-end flex-col">
            <Button
              type="primary"
              :text="hasFilledField ? 'Fortsätt' : 'Hoppa över'"
              :icon="arrowForward"
              @click="onContinue"
            />
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { IonPage, IonContent, IonIcon } from '@ionic/vue';
import { alertCircleOutline, arrowForward, chevronDown } from 'ionicons/icons';
import { useIncidentStore } from '@/store/incident';
import { storeToRefs } from 'pinia';
import FieldInput from '@/components/form-elements/FieldInput.vue';
import {
  IncidentField,
  IncidentRepeaterRow
} from '@/constants/types/api';
import { computed, ref } from 'vue';
import Button from '@/components/Button.vue';

const emits = defineEmits<{
  (e: 'setScrolled', payload: boolean): void;
}>();

const openAccordionItems = ref<number[]>([]);

const incidentStore = useIncidentStore();
const { optionalFields, incident, optionalFieldsLabels, formValidated } = storeToRefs(incidentStore);

type RepeaterRowValueChangePayload = {
  rowIndex: number;
  subFieldLabel: string;
  value: string | null;
};

const onInput = (field: IncidentField, input: string) => {
  field.value = input;
};

const inAdditionalInput = (field: IncidentField, input: string) => {
  field.additional_value = input;
};

const onRepeaterInput = (field: IncidentField, rows: IncidentRepeaterRow[]) => {
  const repeaterField = incidentStore.ensureRepeaterFieldShape(field);
  if (!repeaterField) {
    return;
  }

  repeaterField.rows = rows.map(row => ({ ...row }));
};

const onRepeaterAddRow = (field: IncidentField) => {
  incidentStore.addRepeaterRow(field);
};

const onRepeaterRemoveRow = (field: IncidentField, rowIndex: number) => {
  incidentStore.removeRepeaterRow(field, rowIndex);
};

const onRepeaterRowValueChange = (field: IncidentField, payload: RepeaterRowValueChangePayload) => {
  incidentStore.setRepeaterRowValue(field, payload.rowIndex, payload.subFieldLabel, payload.value);
};

const isFieldInvalid = (field: IncidentField): boolean => {
  return incidentStore.isFieldInvalid(field, true);
};

const openItem = (order: number) => {
  if (openAccordionItems.value.includes(order)) {
    openAccordionItems.value = openAccordionItems.value.filter(item => item !== order);
  } else {
    openAccordionItems.value = [...openAccordionItems.value, order];
  }
};

const handleScroll = (e: CustomEvent) => {
  emits('setScrolled', e.detail.scrollTop > 50);
};

const hasFilledField = computed(() => {
  return !!optionalFields.value.find(field => incidentStore.hasFieldAnswer(field));
});

const onContinue = () => {
  const invalidFieldOrders = optionalFields.value
    .filter(field => incidentStore.isFieldInvalid(field, true))
    .map(field => field.order);

  if (invalidFieldOrders.length > 0) {
    incidentStore.formValidated = true;
    openAccordionItems.value = [...new Set([...openAccordionItems.value, ...invalidFieldOrders])];
    return;
  }

  incidentStore.formValidated = false;
  incidentStore.onNext();
};

</script>
